import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import {
    GetNotesTypesNamesAndIdsDTO,
    GetTranslationsDto,
    INotesOfAnnualReportDTO,
    INotesTemplateDTO,
    INotesTemplateEntityDTO,
    INotesTypeEntityDTO,
    UpdateNotesTypeDto,
    UpdateTranslationsDto,
} from '@modeso/types__aumico-lib-notes-be';
import Debug from 'debug';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AumicoNotesConfigService } from '../aumico-notes.config';
import { INotesTemplateByLegalFormAndAccountBasisRequest } from '../interfaces/requests/INotesTemplateByLegalFormAndAccountBasisRequest.interface';

const debug = Debug('modeso:aumico-lib-notes-fe:AumicoNotesService');

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
    }),
};

@Injectable({
    providedIn: 'root',
})
export class AumicoNotesService {
    private server = '';
    private microserviceName = 'notes';
    private readonly route = '/';
    private readonly notesTemplates = 'notesTemplates';
    private readonly notesTemplate = 'notesTemplate';
    private readonly notesTypes = 'notesTypes';
    private readonly noteType = 'notesType';
    private readonly namesAndIds = 'names-ids';
    private readonly translations = 'translations';

    constructor(
        private http: HttpClient,
        @Inject(AumicoNotesConfigService) private config,
        private coreProvider: AumicoCoreProvider,
    ) {
        this.server = config.apiGateway;
    }

    getNotesTemplates(): Observable<INotesTemplateDTO[]> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.notesTemplates}`;
        this.coreProvider.dispatchBlockingRequest('getNotesTemplates');
        return this.http
            .get<INotesTemplateDTO[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getNotesTemplates')));
    }

    getNotesTemplateByLegalFormAndAccountBasis(combination: {
        payload: INotesTemplateByLegalFormAndAccountBasisRequest;
    }): Observable<INotesOfAnnualReportDTO> {
        const legalFormId: string = combination.payload.legalForm;
        const accountBasisId: string = combination.payload.accountBasis;
        const url = `${this.server}${this.microserviceName}${this.route}${this.notesTemplate}${this.route}${legalFormId}${this.route}${accountBasisId}`;
        this.coreProvider.dispatchBlockingRequest('getNotes');
        return this.http
            .get<INotesOfAnnualReportDTO>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getNotes')));
    }

    getNotesTemplateById(notesTemplateId: string): Observable<INotesTemplateDTO> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.notesTemplates}${this.route}${notesTemplateId}`;
        this.coreProvider.dispatchBlockingRequest('getNotesTemplateById');
        return this.http
            .get<INotesTemplateDTO>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getNotesTemplateById')));
    }

    createNotesTemplate(notesTemplate: { payload: INotesTemplateEntityDTO }): Observable<INotesTemplateDTO> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.notesTemplate}`;
        return this.http.post<INotesTemplateDTO>(url, notesTemplate.payload);
    }

    updateNotesTemplate(notesTemplate: { payload: INotesTemplateEntityDTO }): Observable<INotesTemplateDTO> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.notesTemplate}${this.route}${notesTemplate.payload._id}`;
        return this.http.put<INotesTemplateDTO>(url, notesTemplate.payload);
    }

    getNotesTypes(): Observable<INotesTypeEntityDTO[]> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.notesTypes}`;
        this.coreProvider.dispatchBlockingRequest('getNotesTypes');
        return this.http
            .get<INotesTypeEntityDTO[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getNotesTypes')));
    }

    getNotesTypesNamesAndIds(): Observable<GetNotesTypesNamesAndIdsDTO[]> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.notesTypes}${this.route}${this.namesAndIds}`;
        this.coreProvider.dispatchBlockingRequest('getNotesTypesNamesAndIds');
        return this.http
            .get<GetNotesTypesNamesAndIdsDTO[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getNotesTypesNamesAndIds')));
    }

    createNotesType(notesType: { payload: INotesTypeEntityDTO }): Observable<INotesTypeEntityDTO> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.noteType}`;
        return this.http.post<INotesTypeEntityDTO>(url, notesType.payload);
    }

    updateNotesType(notesType: { payload: UpdateNotesTypeDto }): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.noteType}${this.route}${notesType.payload.noteType._id}`;
        return this.http.put<void>(url, notesType.payload);
    }

    public getTranslations(locales: string[], untranslated: boolean): Observable<GetTranslationsDto> {
        let url = `${this.server}${this.microserviceName}${this.route}${this.translations}`;

        const queryParams = `locales[]=${locales.join('&locales[]=')}&untranslated=${untranslated}`;
        const urlWithParams = `${url}?${queryParams}`;

        this.coreProvider.dispatchBlockingRequest('getTranslations');

        return this.http
            .get<GetTranslationsDto>(urlWithParams, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getTranslations')));
    }

    public updateTranslations(translations: UpdateTranslationsDto): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.route}${this.translations}`;

        this.coreProvider.dispatchBlockingRequest('updateTranslations');
        return this.http
            .patch<void>(url, translations, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateTranslations')));
    }
}
